import { isNil } from 'lodash';
import { isUnauthorizedAtom, requestsQueueAtom, store } from '@/store';

type QueuedRequest = {
  url: string;
  method: string;
  headers: HeadersInit;
  body?: string;
};

const request = () => {
  let baseUrl = '';
  let bearerToken: string | null = null;

  const getFullUrl = (url: string) => {
    return url.startsWith('http') ? url : `${baseUrl}/api${url}`;
  };

  type GetHeadersParams = {
    skipToken?: boolean;
  };

  const getHeaders = ({ skipToken }: GetHeadersParams = {}): HeadersInit => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (skipToken) {
      return headers;
    }

    if (!bearerToken) {
      throw new Error('Bearer token is not set');
    }

    headers['Authorization'] = `Bearer ${bearerToken}`;
    return headers;
  };

  const handleResponse = async <T>(response: Response, req: QueuedRequest): Promise<T> => {
    if (response.status === 401) {
      store.set(isUnauthorizedAtom, true);
      store.set(requestsQueueAtom, (prev) => [...prev, req]);
    }

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || `Error with request to ${req.url}`);
    }

    try {
      const text = await response.text();
      return text ? JSON.parse(text) : ({} as T);
    } catch {
      throw new Error(`Error parsing response from ${req.url}`);
    }
  };

  const getQueryString = (params?: Record<string, any>) => {
    if (!params) {
      return '';
    }

    const filteredEntries = Object.entries(params)
      .filter(([_, value]) => !isNil(value))
      .map(([key, value]) => [key, String(value)]);

    if (!filteredEntries.length) {
      return '';
    }

    return '?' + new URLSearchParams(filteredEntries as [string, string][]).toString();
  };

  const sendRequest = async <T>(
    method: string,
    url: string,
    body?: any,
    options?: { skipToken?: boolean }
  ): Promise<T> => {
    const fullUrl = getFullUrl(url);
    const headers = getHeaders({ skipToken: options?.skipToken });

    const req: QueuedRequest = {
      url: fullUrl,
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    };

    const response = await fetch(fullUrl, {
      method,
      headers,
      body: req.body,
    });

    return handleResponse<T>(response, req);
  };

  return {
    setBaseUrl: (url: string) => {
      baseUrl = url;
    },

    setBearer: (token: string) => {
      bearerToken = token;
    },

    get: async <T, P = Record<string, any>>(url: string, params?: P, options?: GetHeadersParams): Promise<T> => {
      const fullUrl = getFullUrl(url);
      const queryString = getQueryString(params as Record<string, any>);
      const reqUrl = fullUrl + queryString;

      const headers = getHeaders({ skipToken: options?.skipToken });

      const req: QueuedRequest = {
        url: reqUrl,
        method: 'GET',
        headers,
      };

      const response = await fetch(reqUrl, { headers });
      return handleResponse<T>(response, req);
    },

    post: async <T, B = any>(url: string, body?: B, options?: GetHeadersParams): Promise<T> => {
      return sendRequest<T>('POST', url, body, options);
    },

    put: async <T, B = any>(url: string, body?: B): Promise<T> => {
      return sendRequest<T>('PUT', url, body);
    },

    delete: async <T>(url: string): Promise<T> => {
      return sendRequest<T>('DELETE', url);
    },

    upload: async <T>(url: string, formData: FormData): Promise<T> => {
      const fullUrl = getFullUrl(url);

      if (!bearerToken) {
        throw new Error('Bearer token is not set');
      }

      const headers: HeadersInit = {
        Authorization: `Bearer ${bearerToken}`,
      };

      const req: QueuedRequest = {
        url: fullUrl,
        method: 'POST',
        headers,
      };

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers,
        body: formData,
      });

      return handleResponse<T>(response, req);
    },

    request: async <T>(
      url: string,
      options: {
        method: string;
        body?: any;
        skipToken?: boolean;
        headers?: HeadersInit;
      }
    ): Promise<T> => {
      const fullUrl = getFullUrl(url);

      const headers = {
        ...getHeaders({ skipToken: options?.skipToken }),
        ...options.headers,
        Authorization: `Bearer ${bearerToken}`,
      };

      const req: QueuedRequest = {
        url: fullUrl,
        method: options.method,
        headers,
        body: options.body ? JSON.stringify(options.body) : undefined,
      };

      const response = await fetch(fullUrl, {
        method: options.method,
        headers,
        body: req.body,
      });

      return handleResponse<T>(response, req);
    },
  };
};

export default request();
